"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/casting/Investment-Casting/Application";

const ITEMS = [
	{ keyPrefix: "applicationsCard1", image: `${IMAGE_BASE}/aerospace-defense.webp`, href: "/industry/aerospace" },
	{ keyPrefix: "applicationsCard2", image: `${IMAGE_BASE}/automotive-mobility.webp`, href: "/industry/automotive" },
	{ keyPrefix: "applicationsCard3", image: `${IMAGE_BASE}/consumer-technology.webp`, href: "/industry/medical-device" },
	{ keyPrefix: "applicationsCard4", image: `${IMAGE_BASE}/energy-industrial.webp`, href: "/industry/industry-equipment" },
	{ keyPrefix: "applicationsCard5", image: `${IMAGE_BASE}/medical-dental-devices.webp`, href: "/industry/consumer-electronics" },
	{ keyPrefix: "applicationsCard6", image: `${IMAGE_BASE}/robotics-automation.webp`, href: "/industry/robotics-automation" },
] as const;

export default function InvestmentCastingApplications() {
	const t = useTranslations("new-investment-casting");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<p className="mb-2 text-sm font-semibold uppercase tracking-wider text-[#64a525] sm:mb-3">
						{t("applicationsLabel")}
					</p>
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("applicationsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("applicationsDesc")}</p>
				</header>

				<div className="grid grid-cols-1 gap-8 lg:grid-cols-10 lg:gap-12">
					{/* Left: text descriptions */}
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:col-span-6">
						{ITEMS.map((item) => (
							<div key={item.keyPrefix}>
								<h3 className="mb-2 text-base font-bold text-[#64a525] sm:text-lg">
									{t(`${item.keyPrefix}Title`)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">
									{t(`${item.keyPrefix}Desc`)}
								</p>
							</div>
						))}
					</div>

					{/* Right: image cards grid */}
					<div className="grid grid-cols-2 gap-2 sm:gap-3 lg:col-span-4 lg:grid-cols-2">
						{ITEMS.map((item, index) => (
							<Link
								key={item.keyPrefix}
								href={item.href}
								className={`group flex aspect-[4/2.3] flex-col justify-between overflow-hidden rounded-lg p-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-2.5 ${
									index === ITEMS.length - 1
										? "bg-[#88d43c] hover:bg-[#75c72f]"
										: "bg-[#ededed] hover:bg-[#88d43c]"
								}`}
							>
								<div className="relative h-[70px] w-[70px] overflow-hidden rounded-full transition-transform duration-300 group-hover:scale-105">
									<Image
										src={item.image}
										alt={t(`${item.keyPrefix}Alt`)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 25vw, 12vw"
									/>
								</div>
								<p className="self-end whitespace-nowrap text-right text-xs font-semibold leading-tight text-black sm:text-sm">
									{t(`${item.keyPrefix}Title`)}
								</p>
							</Link>
						))}
					</div>
				</div>

				{/* Advantages / Disadvantages */}
				<div className="mt-8 grid grid-cols-1 gap-6 sm:mt-10 md:mt-12 md:grid-cols-2 md:gap-8">
					<div className="p-6 shadow-card sm:p-8">
						<h3 className="mb-4 text-lg font-bold text-black sm:mb-5 sm:text-xl md:text-2xl">
							{t("advantagesTitle")}
						</h3>
						<ul className="flex flex-col gap-3">
							{([1, 2, 3, 4] as const).map((n) => (
								<li
									key={n}
									className="flex items-start gap-2 text-sm leading-relaxed text-black md:text-base"
								>
									<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#64a525]" />
									{t(`advantage${n}`)}
								</li>
							))}
						</ul>
					</div>

					<div className="p-6 shadow-card sm:p-8">
						<h3 className="mb-4 text-lg font-bold text-black sm:mb-5 sm:text-xl md:text-2xl">
							{t("disadvantagesTitle")}
						</h3>
						<ul className="flex flex-col gap-3">
							{([1, 2, 3, 4] as const).map((n) => (
								<li
									key={n}
									className="flex items-start gap-2 text-sm leading-relaxed text-black md:text-base"
								>
									<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#64a525]" />
									{t(`disadvantage${n}`)}
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</section>
	);
}
